"use client";

import { useRef } from "react";
import Image from "next/image";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { IoLogoLinkedin } from "react-icons/io5";

export default function AboutUsExperts({ teamMembers }) {
  const teamRef = useRef(null);

  return (
    <>
      <div className="footer-top-accent" aria-hidden="true" />
      <section className=" bg-background-primary relative overflow-hidden">
        <div className="container max-w-7xl mx-auto! px-6! relative z-10">
          <div className="max-w-4xl mb-12! text-left">
            <p className="text-sm font-bold tracking-[0.2em] uppercase text-purple-600 dark:text-purple-500 mb-4!">OUR EXPERTS</p>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold text-text-primary mb-6! leading-[1.1]">
              Built with <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600 dark:from-purple-500 dark:to-blue-400">passion</span> since{" "}
              <span className="text-purple-600 dark:text-purple-500">2016</span>
            </h2>
            <p className="text-text-secondary text-base md:text-lg leading-relaxed font-light max-w-3xl">
              Tycho Technologies is led by a dynamic team that blends strategic vision, technical expertise, and forward-thinking leadership to build impactful digital solutions.
              Our culture is rooted in collaboration, innovation, and a strong commitment to delivering reliable technology experiences that help businesses grow with confidence.
            </p>
          </div>

          <div className="relative w-full overflow-hidden group">
            <button
              onClick={() => teamRef.current?.scrollBy({ left: -350, behavior: "smooth" })}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-30 w-12 h-12 rounded-full border border-border-primary bg-surface-primary/80 backdrop-blur-md flex items-center justify-center text-text-primary hover:bg-purple-500 hover:text-white hover:border-purple-500 transition-colors shadow-md opacity-100 lg:opacity-0 group-hover:opacity-100 disabled:opacity-0 focus:outline-none"
              aria-label="Previous"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={() => teamRef.current?.scrollBy({ left: 350, behavior: "smooth" })}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-30 w-12 h-12 rounded-full border border-border-primary bg-surface-primary/80 backdrop-blur-md flex items-center justify-center text-text-primary hover:bg-purple-500 hover:text-white hover:border-purple-500 transition-colors shadow-md opacity-100 lg:opacity-0 group-hover:opacity-100 disabled:opacity-0 focus:outline-none"
              aria-label="Next"
            >
              <ChevronRight size={24} />
            </button>

            <div
              ref={teamRef}
              className="flex !gap-4 md:gap-6 overflow-x-auto snap-x snap-mandatory !pb-8 !px-2 md:px-4"
              style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
            >
              {[...teamMembers].map((member, i) => (
                <div
                  key={i}
                  className="w-[280px] md:w-[260px] shrink-0 snap-start border border-border-primary hover:border-purple-500/50 rounded-2xl overflow-hidden transition-all duration-500 group/card flex flex-col"
                  style={{
                    background: "var(--card-background)",
                    boxShadow: "var(--card-shadow)",
                  }}
                >
                  <div className="relative h-[320px] overflow-hidden">
                    <Image
                      src={member.image}
                      alt={member.name}
                      fill
                      className="object-cover object-top transition-transform duration-700 group-hover/card:scale-105"
                      onError={(e) => {
                        e.target.src = `https://via.placeholder.com/300x400/3b5bdb/ffffff?text=${member.name[0]}`;
                      }}
                    />

                    <div className="absolute bottom-0 left-0 right-0 !p-1 !pt-1 text-center bg-gradient-to-t from-black/90 via-black/60 to-transparent backdrop-blur-xs">
                      <h3 className="font-display font-light text-lg text-white">{member.name}</h3>
                      <p className="text-gray-200 text-xs font-light !mb-1">{member.designation}</p>
                    </div>
                  </div>
                  <div className="!p-3 bg-surface-secondary border-t border-border-primary">
                    <a
                      href={member.linkedinLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center !gap-3 text-text-primary hover:text-primary font-medium transition-colors text-sm"
                    >
                      Connect Now
                      <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center shrink-0 shadow-md">
                        <IoLogoLinkedin size={14} className="text-text-inverse" />
                      </div>
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
